import React, { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { ReportSection } from './ReportSection';
import { formatReportDelta } from '../../utils/reportFormat';
import type { ClubStoreMode, MonthlyClubReport } from '../../types/monthlyClubReport';
const changeClass = (change: number) => change > 0 ? 'text-[#10B981]' : change < 0 ? 'text-[#EF4444]' : 'text-[#64748B]';
interface RowProps {
  label: React.ReactNode;
  members: number;
  change: number;
  totalMembers: number;
  variant: 'group' | 'pack' | 'nested';
}
function MembershipRow({
  label,
  members,
  change,
  totalMembers,
  variant
}: RowProps) {
  const share = totalMembers ? members / totalMembers * 100 : 0;
  const isGroup = variant === 'group';
  return <tr className={`border-b border-[#E2E8F0] ${isGroup ? 'bg-[#F8FAFC]' : ''}`} data-id="element-3583" data-mp="1zwkqmiazwizu">
      <td className={`py-3 pr-4 text-[#0F172A] ${isGroup ? 'font-semibold' : ''} ${variant === 'nested' ? 'pl-8' : ''}`} data-id="element-3584" data-mp="24xi4wjsvwsw2">
        {label}
      </td>
      <td className={`py-3 px-4 text-right text-[#0F172A] ${isGroup ? 'font-semibold' : 'font-medium'}`} data-id="element-3585" data-mp="aulu1xpnl5j2">
        {members.toLocaleString('en-AU')}
      </td>
      <td className="py-3 px-4" data-id="element-3586" data-mp="5tz76i8dcg77">
        <div className="flex items-center gap-3" data-id="element-3587" data-mp="5xvercloadyp">
          <div className="flex-1 h-2 rounded-full bg-[#F1F5F9] overflow-hidden" aria-hidden="true" data-id="element-3588" data-mp="1uyulhghi9sn">
            <div className={`h-full rounded-full ${isGroup ? 'bg-[#0284C7]' : 'bg-[#0EA5E9]'}`} style={{
            width: `${share}%`
          }} data-id="element-3589" data-mp="3t1jpxddko3vh" />
          </div>
          <span className={`w-12 text-right ${isGroup ? 'font-semibold text-[#0F172A]' : 'text-[#475569]'}`} data-id="element-3590" data-mp="pjrt9lohqvqy">
            {share.toFixed(1)}%
          </span>
        </div>
      </td>
      <td className={`py-3 pl-4 text-right ${isGroup ? 'font-semibold' : 'font-medium'} ${changeClass(change)}`} data-id="element-3591" data-mp="ftq2gm4r6z8c">
        {formatReportDelta(change, 'number')}
      </td>
    </tr>;
}
export function MembersByClubSection({
  report,
  storeMode
}: {
  report: MonthlyClubReport;
  storeMode: ClubStoreMode;
}) {
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const isOneClub = storeMode === 'one';
  const totalMembers = isOneClub ? report.singleClubPacks.reduce((sum, p) => sum + p.members, 0) : report.clubs.reduce((sum, c) => sum + c.members, 0);
  const totalChange = isOneClub ? report.singleClubPacks.reduce((sum, p) => sum + p.changeVsPrev, 0) : report.clubs.reduce((sum, c) => sum + c.changeVsPrev, 0);
  const toggleClub = (name: string) => setCollapsed(prev => {
    const next = new Set(prev);
    if (next.has(name)) next.delete(name);else next.add(name);
    return next;
  });
  return <ReportSection id="members-by-club" title={isOneClub ? 'Members by pack and frequency' : 'Members by club'} data-id="element-3592" data-mp="znydoby5pdho">
      <div className="overflow-x-auto" data-id="element-3593" data-mp="13o9zvt663lv9">
        <table className="w-full text-sm" data-id="element-3594" data-mp="13pbiqjtfdcnj">
          <thead data-id="element-3595" data-mp="2u0y1utglrg0k">
            <tr className="border-b border-[#E2E8F0] text-[12px] font-semibold text-[#94A3B8] uppercase tracking-[0.5px]" data-id="element-3596" data-mp="2u2iwsk6tx0yy">
              <th scope="col" className="text-left py-2 pr-4" data-id="element-3597" data-mp="2y2jb84txhc60">
                {isOneClub ? 'Pack and frequency' : 'Club / pack and frequency'}
              </th>
              <th scope="col" className="text-right py-2 px-4" data-id="element-3598" data-mp="iex7rixzqbli">Members</th>
              <th scope="col" className="text-left py-2 px-4 w-[30%]" data-id="element-3599" data-mp="w5ivbkye39pq">Share of all members</th>
              <th scope="col" className="text-right py-2 pl-4" data-id="element-3600" data-mp="respy7pyr6v1">Change vs {report.prevShort}</th>
            </tr>
          </thead>
          <tbody data-id="element-3601" data-mp="162x7v9h6zecr">
            {isOneClub ? report.singleClubPacks.map(pack => <MembershipRow key={pack.label} label={pack.label} members={pack.members} change={pack.changeVsPrev} totalMembers={totalMembers} variant="pack" data-id="element-3602" data-mp="114m0d5eddfxp" />) : report.clubs.map(club => {
            const isOpen = !collapsed.has(club.name);
            return <React.Fragment key={club.name} data-id="element-3603" data-mp="1a6lhrytaqm16">
                    <MembershipRow variant="group" members={club.members} change={club.changeVsPrev} totalMembers={totalMembers} label={<button type="button" onClick={() => toggleClub(club.name)} aria-expanded={isOpen} className="inline-flex items-center gap-1.5 text-left hover:text-[#0EA5E9] transition-colors" data-id="element-3605" data-mp="3bx6n71yjqc1o">
                          <ChevronDown className={`w-4 h-4 text-[#94A3B8] transition-transform print:hidden ${isOpen ? '' : '-rotate-90'}`} data-id="element-3606" data-mp="3icdtvq9slg7f" />
                          {club.name}
                        </button>} data-id="element-3604" data-mp="3gvrrpdktzhvx" />
                    {isOpen && club.packs.map(pack => <MembershipRow key={pack.label} label={pack.label} members={pack.members} change={pack.changeVsPrev} totalMembers={totalMembers} variant="nested" data-id="element-3607" data-mp="3sabiwfzh7h13" />)}
                  </React.Fragment>;
          })}
            <tr className="font-semibold text-[#0F172A]" data-id="element-3608" data-mp="2zkcr4ctwd5sq">
              <td className="py-3 pr-4" data-id="element-3609" data-mp="34kh5nxb8vqgb">Total</td>
              <td className="py-3 px-4 text-right" data-id="element-3610" data-mp="34jpdyxkrqcvb">{totalMembers.toLocaleString('en-AU')}</td>
              <td className="py-3 px-4" data-id="element-3611" data-mp="39e1xt8q5pq7l">
                <span className="block text-right" data-id="element-3612" data-mp="2bxmopadmz97l">100%</span>
              </td>
              <td className={`py-3 pl-4 text-right ${changeClass(totalChange)}`} data-id="element-3613" data-mp="2gvo8xymstekn">
                {formatReportDelta(totalChange, 'number')}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </ReportSection>;
}